/**
 * Vue d'ensemble des caractéristiques — l'écran qui manquait.
 *
 * Les fichiers d'usine réels portent quinze à trente-deux colonnes de mesure. Afficher
 * une carte de contrôle à la fois obligeait l'utilisateur à cliquer trente fois pour
 * savoir laquelle pose problème. Cet écran répond en trois secondes à la seule question
 * qui compte : **où est le problème ?**
 *
 * Deux partis pris.
 *
 * **Le tri par défaut est l'instabilité, pas l'ordre du fichier.** Un tableau trié comme
 * la source oblige à tout lire. Trié par gravité, la première ligne est la réponse.
 *
 * **On parle de stabilité, pas de capabilité.** La capabilité suppose des tolérances, et
 * huit fichiers sur dix n'en portent aucune. Annoncer un Cpk sans spécification serait
 * inventer un chiffre. Le contrôle statistique, lui, se calcule sans tolérance : un
 * procédé peut être parfaitement stable et hors tolérance, ou l'inverse, et c'est
 * exactement la distinction qu'un tableur ne fait jamais.
 */

import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";

import type { AnalysedChart } from "./mappingApi";
import { DiagnosisDot } from "./DiagnosisPanel";
import { Sparkline } from "./ui/Sparkline";
import { Badge, Td, Th, cn, num } from "./ui/primitives";

export interface Row {
  code: string;
  n: number;
  /** Effectif de la série complète, dont `n` est le segment retenu. */
  total: number;
  mean: number;
  sigma: number;
  violations: number;
  /** Part des points signalés par au moins une règle de Nelson. */
  rate: number;
  chart: AnalysedChart;
}

/** Aucun calcul statistique ici.
 *
 *  Moyenne, écart-type et nombre de violations viennent du serveur, où ils sont établis
 *  sur la série **complète**. Les points reçus, eux, sont réduits pour l'affichage : les
 *  recalculer à partir d'eux donnerait des chiffres proches mais faux, et un chiffre
 *  faux dans un tableau de mesures détruit la confiance dans tout l'écran. */
export function buildRows(charts: AnalysedChart[]): Row[] {
  return charts.map((chart) => ({
    code: chart.characteristic_code,
    n: chart.points_total || chart.points.length,
    total: chart.series_total,
    mean: chart.mean,
    sigma: chart.sigma,
    violations: chart.violations_total,
    rate: chart.rules_evaluated_on
      ? chart.violations_total / chart.rules_evaluated_on
      : 0,
    chart,
  }));
}

/** Seuils de lecture, empiriques et assumés comme tels.
 *
 *  Sur un procédé sous contrôle, les règles de Nelson produisent quelques fausses
 *  alertes — c'est leur nature statistique. En dessous de 1 % de points signalés, le
 *  bruit domine ; au-delà de 5 %, il se passe quelque chose. */
const NOISE = 0.01;
const ALARM = 0.05;

function stability(rate: number): {
  tone: "good" | "warn" | "bad";
  label: string;
} {
  if (rate >= ALARM) return { tone: "bad", label: "Hors contrôle" };
  if (rate >= NOISE) return { tone: "warn", label: "À surveiller" };
  return { tone: "good", label: "Sous contrôle" };
}

type SortKey = "rate" | "code" | "n" | "sigma";

export function CharacteristicsTable({
  rows,
  selected,
  onSelect,
}: {
  rows: Row[];
  selected: string | null;
  onSelect: (code: string) => void;
}) {
  const [key, setKey] = useState<SortKey>("rate");
  const [asc, setAsc] = useState(false);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const value =
        key === "code"
          ? a.code.localeCompare(b.code, "fr")
          : (a[key] as number) - (b[key] as number);
      return asc ? value : -value;
    });
    return copy;
  }, [rows, key, asc]);

  const sortBy = (next: SortKey) => {
    if (next === key) setAsc((v) => !v);
    else {
      setKey(next);
      // Les libellés se lisent de A à Z, les mesures du plus grave au moins grave.
      setAsc(next === "code");
    }
  };

  const header = (id: SortKey, label: string, className?: string) => (
    <Th
      className={cn("cursor-pointer select-none hover:text-ink", className)}
      onClick={() => sortBy(id)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {key === id &&
          (asc ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          ))}
      </span>
    </Th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[0.82rem]">
        <thead>
          <tr>
            {header("code", "Caractéristique")}
            {header("n", "n", "text-right")}
            <Th>Moyenne</Th>
            {header("sigma", "σ")}
            <Th>Évolution</Th>
            {header("rate", "Points signalés", "text-right")}
            <Th>État</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, position) => {
            const state = stability(row.rate);
            const active = row.code === selected;
            return (
              <tr
                key={row.code}
                onClick={() => onSelect(row.code)}
                style={{ animationDelay: `${Math.min(position, 14) * 18}ms` }}
                className={cn(
                  "animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] cursor-pointer",
                  "transition-colors hover:bg-surface-2",
                  active && "bg-accent-soft hover:bg-accent-soft",
                )}
              >
                <Td className="font-medium">
                  <span className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-3.5 w-0.5 shrink-0 rounded-full",
                        active ? "bg-accent" : "bg-transparent",
                      )}
                    />
                    <span className="font-mono text-[0.78rem]">{row.code}</span>
                  </span>
                </Td>
                <Td className="tnum text-right text-muted">
                  {num(row.n)}
                  {row.total > row.n && (
                    <span className="text-faint"> / {num(row.total)}</span>
                  )}
                </Td>
                <Td className="tnum text-muted">{row.mean.toFixed(3)}</Td>
                <Td className="tnum text-muted">{row.sigma.toFixed(4)}</Td>
                <Td>
                  <Sparkline
                    points={row.chart.points}
                    violations={row.chart.violation_indexes}
                    cpk={
                      state.tone === "good"
                        ? 2
                        : state.tone === "warn"
                          ? 1.1
                          : 0.5
                    }
                  />
                </Td>
                <Td className="tnum text-right">
                  <span
                    className={cn(
                      row.violations > 0 ? "text-ink" : "text-faint",
                    )}
                  >
                    {row.violations > 0
                      ? `${num(row.violations)} · ${(row.rate * 100).toFixed(1)} %`
                      : "aucun"}
                  </span>
                </Td>
                <Td>
                  <Badge tone={state.tone}>{state.label}</Badge>
                  <DiagnosisDot diagnosis={row.chart.diagnosis} />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
